/**
 * Grey Bandit — PDP Color Swatch AJAX Navigation
 * ------------------------------------------------
 * Intercepts clicks on the PDP color swatch links and swaps only the sections
 * of the page that are unique per product, instead of performing a full page
 * reload.  Uses Shopify's Sections Rendering API to batch-fetch all three
 * target sections in a single request.
 *
 * SECTIONS SWAPPED
 * ----------------
 * 1. .shopify-section--main-product           — product info, gallery, ATC
 * 2. .shopify-section--complimentary-products — cross-sells
 * 3. .shopify-section--apps (first one)       — Foursixty image feed
 *
 * HOW IT WORKS
 * ------------
 * 1. A delegated click listener on `document` catches any click inside the
 *    PDP color swatch list (.swatch-products .ColorSwatchList a).
 * 2. The default navigation is cancelled.
 * 3. The section IDs are read from the current page's DOM (by stripping the
 *    "shopify-section-" prefix from each element's id attribute).
 * 4. The swatch link's full href — including any ?variant=...&Size=... params
 *    the theme has already set — is passed to the Sections API so Shopify
 *    renders the correct pre-selected variant/size in the new sections.
 * 5. A single fetch is made to:
 *      /products/{handle}?variant=X&Size=Y&sections={id1},{id2},{id3}
 * 6. Each existing section element is replaced via outerHTML.
 * 7. The browser URL is updated with history.replaceState (not pushState) so
 *    the address bar reflects the new product without adding a history entry —
 *    meaning Back always returns the customer to wherever they came from, not
 *    through every color they browsed.
 * 8. Foursixty is re-initialised after the apps section is swapped by calling
 *    Foursixty.Slider.create() to get a new Embed instance, then calling
 *    instance.init(targetElement) with the freshly-swapped container element.
 *    The library is already loaded in memory; the constructor does not
 *    auto-call init(), so both steps are required.
 * 9. The Afterpay "or 4 interest-free payments" widget is re-injected after
 *    the main section swap.  The widget is a <square-placement> Web Component
 *    dynamically injected by the Afterpay Shopify snippet on page load — it is
 *    NOT part of the server-rendered section HTML, so it must be recreated.
 *    /products/{handle}.js is fetched in parallel with the sections call to
 *    obtain the price and SKU for the selected variant; the element is then
 *    constructed from those values plus the Afterpay globals the snippet sets.
 *
 * INSTALLATION
 * ------------
 * Add as a theme asset and include with <script defer src="..."> in
 * theme.liquid, or paste into an existing deferred script block.
 *
 * TESTED ON: Grey Bandit theme — greybandit.com — Feb 2026
 */

(function () {
  'use strict';

  // ─── Selectors ────────────────────────────────────────────────────────────

  /** CSS selectors for the three sections that are unique per product. */
  var SECTION_SELECTORS = [
    '.shopify-section--main-product',
    '.shopify-section--complimentary-products',
    '.shopify-section--apps',
  ];

  /**
   * Selector for the color swatch anchor tags on the PDP.
   * These live inside: .swatch-products > .ColorSwatchList > li > a
   * and are scoped to the main product section so we don't accidentally
   * intercept swatch links in other parts of the page.
   */
  var SWATCH_LINK_SELECTOR =
    '.shopify-section--main-product .swatch-products .ColorSwatchList li a';

  // ─── Helpers ──────────────────────────────────────────────────────────────

  /**
   * Read the three target section IDs from the live DOM.
   * Shopify section wrapper elements have id="shopify-section-{sectionId}".
   * The Sections Rendering API takes the sectionId part (without the prefix).
   *
   * Deriving IDs from the DOM rather than hardcoding them means the script
   * keeps working even if the theme's section names change.
   *
   * @returns {Array<{elementId: string, sectionId: string}>}
   */
  function getSectionIds() {
    return SECTION_SELECTORS
      .map(function (sel) { return document.querySelector(sel); })
      .filter(Boolean)
      .map(function (el) {
        return {
          elementId: el.id,
          sectionId: el.id.replace('shopify-section-', ''),
        };
      });
  }

  /**
   * Extract the product handle from any URL containing /products/{handle}.
   * Works with absolute URLs, root-relative paths, and paths with query strings.
   *
   * @param {string} url
   * @returns {string|null}
   */
  function extractHandle(url) {
    var match = (url || '').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  // ─── Loading state ────────────────────────────────────────────────────────

  /**
   * Dim the main product section and disable pointer events while fetching,
   * giving the customer immediate visual feedback that something is happening.
   *
   * @param {boolean} on
   */
  function setLoading(on) {
    var el = document.querySelector('.shopify-section--main-product');
    if (!el) return;
    el.style.transition    = on ? 'opacity 0.15s ease' : '';
    el.style.opacity       = on ? '0.45' : '';
    el.style.pointerEvents = on ? 'none' : '';
  }

  // ─── Foursixty re-initialisation ──────────────────────────────────────────

  /**
   * After the apps section is replaced, the Foursixty image feed's container
   * element is fresh (empty) but its init script won't re-run because browsers
   * don't execute <script> tags inserted via outerHTML.  window.Foursixty is
   * already loaded from the original page.
   *
   * The correct re-initialisation sequence (discovered by inspecting the
   * Foursixty library internals) is:
   *   1. Call api.create() — returns a new Embed instance with options set.
   *   2. Find the fresh target element in the newly-swapped section HTML.
   *   3. Call instance.init(targetElement) — this sets instance.targetDiv
   *      and triggers the async post-fetch.  Calling create() alone is a
   *      no-op because the constructor does not auto-call init().
   *
   * The targetSelector (e.g. "#foursixty-AL3dWSGMydHhJQlkyO__...") is stored
   * in api.theme and matches the same element ID in the fresh section HTML.
   */
  function reinitFoursixty() {
    var FS = window.Foursixty;
    if (!FS) return;

    // Prefer Slider (the widget used on this theme); fall back to Embed
    var api = (FS.Slider && typeof FS.Slider.create === 'function') ? FS.Slider
            : (FS.Embed  && typeof FS.Embed.create  === 'function') ? FS.Embed
            : null;
    if (!api) return;

    // The targetSelector stored in api.theme points to the element ID that
    // Foursixty renders into — the same ID is present in the freshly-swapped
    // section HTML, so querySelector will find the new (empty) element.
    var targetSelector = api.theme && api.theme.targetSelector;
    if (!targetSelector) return;

    var targetEl = document.querySelector(targetSelector);
    if (!targetEl) return;

    try {
      var instance = api.create();
      if (instance && typeof instance.init === 'function') {
        instance.init(targetEl);
      }
    } catch (_) { /* non-fatal */ }
  }

  // ─── Afterpay re-injection ────────────────────────────────────────────────

  /**
   * Re-inject the Afterpay <square-placement> widget after the main product
   * section is swapped.
   *
   * WHY THIS IS NECESSARY
   * The Afterpay Shopify snippet dynamically injects <square-placement> into
   * #product-square-selector on page load.  The element is NOT part of the
   * Shopify liquid section HTML, so the sections API response only contains the
   * empty #product-square-selector / #product-price-selector containers.
   * After outerHTML replacement those containers are fresh but empty.
   *
   * HOW IT WORKS
   * <square-placement> is a registered custom element (Web Component).
   * Creating one via document.createElement and inserting it into the DOM fires
   * connectedCallback immediately, which attaches the shadow root and renders
   * the "or N interest-free payments" widget without any additional JS calls.
   *
   * The static display attributes (logo theme, size, currency, locale, etc.)
   * are read from the window.afterpay_* globals the snippet sets on page load.
   * The dynamic per-product attributes (data-amount, data-item-skus) come from
   * the product JSON passed in by switchProduct(), which fetches
   * /products/{handle}.js in parallel with the sections API call.
   *
   * @param {Object} product       — Shopify product JSON from /products/{handle}.js
   * @param {string} variantId     — selected variant ID string (may be null)
   */
  function reinitAfterPay(product, variantId) {
    // Only proceed if Afterpay product integration is active and the
    // <square-placement> custom element has been registered by the snippet.
    if (!window.afterpay_product_integration_enabled) return;
    if (!customElements.get('square-placement')) return;

    var container = document.getElementById('product-square-selector');
    if (!container) return;

    // Remove any stale placement that survived the outerHTML swap (shouldn't
    // happen, but guard against duplicates just in case).
    var stale = container.querySelectorAll('square-placement');
    stale.forEach(function (el) { el.parentNode.removeChild(el); });

    // Resolve the variant whose price and SKU we'll display.
    // Prefer the explicitly selected variant (from the swatch href); fall back
    // to the first available variant.
    var variant = null;
    var variantIdNum = variantId ? Number(variantId) : null;
    if (variantIdNum && product.variants) {
      for (var i = 0; i < product.variants.length; i++) {
        if (product.variants[i].id === variantIdNum) {
          variant = product.variants[i];
          break;
        }
      }
    }
    if (!variant && product.variants && product.variants.length) {
      variant = product.variants[0];
    }
    if (!variant) return;

    // Build the <square-placement> element.
    // Static attributes are sourced from window.afterpay_* globals; dynamic
    // ones (amount, sku) come from the resolved variant.
    var sp = document.createElement('square-placement');

    // Static display configuration — set once by the snippet, constant across products
    sp.setAttribute('data-mpid',                   window.afterpay_shop_permanent_domain || '');
    sp.setAttribute('data-currency',               window.afterpay_shop_currency         || 'USD');
    sp.setAttribute('data-platform',               'shopify-snippet');
    sp.setAttribute('data-consumer-locale',        (window.afterpay_js_language || 'en') + '_' + (window.afterpay_js_country || 'US'));
    sp.setAttribute('data-show-upper-limit',       window.afterpay_hide_upper_limit      ? 'false' : 'true');
    sp.setAttribute('data-show-lower-limit',       window.afterpay_hide_lower_limit      ? 'false' : 'true');
    sp.setAttribute('data-show-if-outside-limits', window.afterpay_show_if_outside_limits ? 'true' : 'false');
    sp.setAttribute('data-payment-amount-is-bold', window.afterpay_bold_amount           ? 'true'  : 'false');
    sp.setAttribute('data-size',                   window.afterpay_msg_size              || 'sm');
    sp.setAttribute('data-logo-type',              'lockup');
    sp.setAttribute('data-lockup-theme',           window.afterpay_logo_theme            || 'black');
    sp.setAttribute('data-page-type',              'product');

    // Dynamic attributes — vary per product / variant
    // Shopify stores prices in cents; Afterpay data-amount is in whole dollars.
    sp.setAttribute('data-amount',       String(variant.price / 100));
    sp.setAttribute('data-item-skus',    variant.sku || '');
    sp.setAttribute('data-item-categories', '');

    // Unique ID in the same format the snippet uses
    sp.setAttribute('id', 'placement-' + Date.now());
    sp.style.marginTop    = '0px';
    sp.style.marginBottom = '0px';

    // Insert immediately after #product-price-selector, matching the original
    // placement the Afterpay snippet produces.
    var priceSelector = document.getElementById('product-price-selector');
    if (priceSelector) {
      priceSelector.insertAdjacentElement('afterend', sp);
    } else {
      container.appendChild(sp);
    }
  }

  // ─── Core switch ──────────────────────────────────────────────────────────

  /** Prevent concurrent switches if the customer clicks quickly. */
  var isSwitching = false;

  /**
   * Fetch the target product's sections and swap them into the page.
   *
   * Two requests are fired concurrently via Promise.all:
   *   • Sections API — returns the HTML for all three swapped sections.
   *   • /products/{handle}.js — lightweight JSON with price + SKU per variant,
   *     needed to re-inject the Afterpay widget with the correct installment amount.
   *
   * @param {string} href — full href of the target product link, which the
   *                        theme may have already enriched with ?variant=...&Size=...
   *                        params so the correct size is pre-selected in the render.
   */
  function switchProduct(href) {
    if (isSwitching) return;

    var handle = extractHandle(href);
    if (!handle) return;

    // Don't re-fetch if we're already on this product
    if (handle === extractHandle(window.location.pathname)) return;

    var sections = getSectionIds();
    if (!sections.length) return;

    isSwitching = true;
    setLoading(true);

    // Extract the selected variant ID from the href, if present.
    // Used later to resolve the right price/SKU for the Afterpay widget.
    var variantMatch = href.match(/[?&]variant=(\d+)/);
    var selectedVariantId = variantMatch ? variantMatch[1] : null;

    // Build the Sections Rendering API URL.
    //
    // The swatch link href already carries any ?variant=...&Size=... params
    // that the theme dynamically sets when the customer selects a size, so we
    // preserve all of those and simply add `sections` to the same query string.
    // This tells Shopify to render the new sections with the correct variant
    // pre-selected — matching the size the customer had already picked.
    var targetUrl    = new URL(href, window.location.origin);
    var searchParams = new URLSearchParams(targetUrl.search);
    var sectionIds   = sections.map(function (s) { return s.sectionId; }).join(',');
    searchParams.set('sections', sectionIds);
    var fetchUrl = targetUrl.pathname + '?' + searchParams.toString();

    // Fire both requests concurrently.
    // The product JSON fetch is best-effort: if it fails we still do the swap,
    // the Afterpay widget just won't be re-injected that time.
    var sectionsPromise = fetch(fetchUrl).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });

    var productPromise = fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .catch(function () { return null; }); // non-fatal

    Promise.all([sectionsPromise, productPromise])
      .then(function (results) {
        var data    = results[0];
        var product = results[1]; // may be null if fetch failed

        // ── Swap each section ────────────────────────────────────────────
        sections.forEach(function (section) {
          var newHtml  = data[section.sectionId];
          var existing = document.getElementById(section.elementId);
          if (newHtml && existing) {
            // outerHTML replacement removes the old element and inserts the
            // new one in place.  Shopify custom elements initialise themselves
            // via connectedCallback when attached to the DOM.
            existing.outerHTML = newHtml;
          }
        });

        // ── Re-inject Afterpay widget ────────────────────────────────────
        // Must run after the main section outerHTML swap so the fresh
        // #product-square-selector container exists in the DOM.
        if (product) {
          reinitAfterPay(product, selectedVariantId);
          _swat.initializeActionButtons('.product');
        }

        // ── Re-initialise Foursixty ──────────────────────────────────────
        // Must run after the apps section outerHTML has been replaced so the
        // fresh container element exists in the DOM before create() targets it.
        reinitFoursixty();

        // ── Update browser URL (replace, not push) ───────────────────────
        // Using replaceState means swatch clicks don't stack up as separate
        // history entries.  The customer presses Back once to return to
        // wherever they came from (e.g. the collection page), not once per
        // color they browsed.
        history.replaceState(null, '', href);

        // ── Update page title ────────────────────────────────────────────
        // Extract the product name from the new section's h1, then reassemble
        // the title using the same separator (– or |) the theme already uses.
        var h1 = document.querySelector('.shopify-section--main-product h1');
        if (h1) {
          var sepMatch  = document.title.match(/\s([–—|])\s/);
          var sep       = sepMatch ? (' ' + sepMatch[1] + ' ') : ' – ';
          var parts     = document.title.split(sep);
          var storeName = parts.length > 1 ? parts[parts.length - 1] : '';
          document.title = h1.textContent.trim() + (storeName ? sep + storeName : '');
        }

        // ── Scroll correction ─────────────────────────────────────────────
        // If the customer had scrolled well past the top of the product section
        // (e.g. reading reviews), bring them back up to the new product content.
        var mainEl = document.querySelector('.shopify-section--main-product');
        if (mainEl && mainEl.getBoundingClientRect().top < -200) {
          mainEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        let urlParams = new URLSearchParams(window.location.search);
        let sizeParam = urlParams.get('Size');
        if (sizeParam) {
          document.querySelectorAll(".ColorSwatchList a").forEach(link => {
            var current = link.href.split('?')[0];
            var varId = link.getAttribute('data-size-' + sizeParam);
            link.href = current + '?variant=' + varId + '&Size=' + sizeParam;
          }); 
        } 

        isSwitching = false;
        setLoading(false);
      })
      .catch(function () {
        // On any network or parse error, fall back to a normal page navigation
        // so the customer always ends up on the right product.
        isSwitching = false;
        setLoading(false);
        window.location.href = href;
      });
  }

  // ─── Event listener ───────────────────────────────────────────────────────

  /**
   * Delegated click handler.
   *
   * Listening on `document` rather than the swatch list element directly means
   * the handler keeps working after the main product section is replaced in the
   * DOM (the section element itself is destroyed and re-created on each switch).
   */
  document.addEventListener('click', function (e) {
    var link = e.target.closest(SWATCH_LINK_SELECTOR);
    if (!link) return;

    var href = link.getAttribute('href');
    if (!href || href.indexOf('/products/') === -1) return;

    // Clicking the already-active swatch should do nothing
    if (extractHandle(href) === extractHandle(window.location.pathname)) return;

    e.preventDefault();
    switchProduct(href);
  });

})();
